import type { ChartOptions } from "./dashboardHelpers";

/**
 * Single source of truth for two questions the manual chart controls keep asking:
 *
 *   1. Which axis does each scale belong to, and which scales does a dataset draw on?
 *      ({@link axesBySide} — Chart.js's own rule, so the override layer touches the scales
 *      the chart actually has instead of a literal `x`/`y`. 7% of production charts name
 *      their value axes `yLeft`/`yRight`, `yConv`/`yUsers`, `y1`, ... — writing `scales.y`
 *      on those creates a stray empty axis.)
 *
 *   2. Which controls does a chart of this type get? ({@link getChartCapabilities} — one row
 *      per chart_type, read by the edit popover for what to show and by the apply layer for
 *      what to honor, so a new chart type cannot fall through with an undefined set of
 *      controls. Adding a type without a row is a compile error via `satisfies`.)
 */

type AnyRecord = Record<string, any>;

export type AxisSide = "x" | "y";

export interface ResolvedAxes {
  /** Scale ids on the horizontal side, in config order. `["x"]` when the chart defines none. */
  x: string[];
  /** Scale ids on the vertical side, in config order. `["y"]` when the chart defines none. */
  y: string[];
  /** Which side carries the categories (`options.indexAxis`, default "x"). */
  indexSide: AxisSide;
  /** The other side: where values are plotted. */
  valueSide: AxisSide;
}

const axisFromPosition = (position: unknown): AxisSide | undefined => {
  if (position === "left" || position === "right") {
    return "y";
  }
  if (position === "top" || position === "bottom") {
    return "x";
  }
  return undefined;
};

/**
 * Chart.js 4.4 `determineAxis`: an id of exactly `x`/`y` is that axis; else the scale's `axis`
 * option, else its `position`, else the id's first letter when the id is longer than one
 * character. Radial (`r`) and anything unresolvable is not a cartesian scale and is skipped.
 */
export function axisSideOfScale(id: string, scale: AnyRecord | undefined): AxisSide | undefined {
  if (id === "x" || id === "y") {
    return id;
  }
  if (id === "r") {
    return undefined;
  }
  const explicit = scale?.axis;
  if (explicit === "x" || explicit === "y") {
    return explicit;
  }
  const byPosition = axisFromPosition(scale?.position);
  if (byPosition) {
    return byPosition;
  }
  const first = id.length > 1 ? id[0].toLowerCase() : "";
  return first === "x" || first === "y" ? (first as AxisSide) : undefined;
}

export function axesBySide(chartOptions: Pick<ChartOptions, "options">): ResolvedAxes {
  const options = (chartOptions.options ?? {}) as AnyRecord;
  const scales = (options.scales ?? {}) as AnyRecord;
  const x: string[] = [];
  const y: string[] = [];
  for (const id of Object.keys(scales)) {
    const side = axisSideOfScale(id, scales[id]);
    if (side === "x") {
      x.push(id);
    } else if (side === "y") {
      y.push(id);
    }
  }
  const indexSide: AxisSide = options.indexAxis === "y" ? "y" : "x";
  return {
    x: x.length ? x : ["x"],
    y: y.length ? y : ["y"],
    indexSide,
    valueSide: indexSide === "x" ? "y" : "x",
  };
}

/** How many distinct value scales the chart plots on (1 for the common case, 2 for dual-axis). */
export function countValueAxes(chartOptions: Pick<ChartOptions, "options">): number {
  const axes = axesBySide(chartOptions);
  return axes[axes.valueSide].length;
}

// ---------------------------------------------------------------------------------------------
// Per-type capabilities
// ---------------------------------------------------------------------------------------------

/** What the stacking dropdown offers: hidden, None/Stacked, or None/Stacked/% Stacked. */
export type StackingCapability = "hidden" | "plain" | "full";

export interface ChartCapabilities {
  /** Show the in-place type switcher (the categorical {labels, datasets[]} family). */
  typeSwitch: boolean;
  /**
   * For types the switcher can't convert mechanically: chart_types an AI rebuild sensibly
   * targets (the source data supports them). Empty means only "describe it in chat".
   */
  aiConvertTargets: readonly string[];
  stacking: StackingCapability;
  legend: boolean;
  dataLabels: boolean;
  axes: boolean;
}

/** Facts about the specific chart that refine the per-type row. */
export interface ChartCapabilityContext {
  /** Dataset count; stacking is pointless below 2. */
  seriesCount: number;
  /** Distinct value axes; percent stacking has no meaning across two. */
  valueAxisCount: number;
  /** Whether the render JS configured `plugins.datalabels` itself. */
  hasOwnDatalabels: boolean;
}

type CapabilityRow = Omit<ChartCapabilities, "stacking"> & {
  /**
   * "series": stacking follows the series and axis count (cartesian bar/line family).
   * "hidden": never offered.
   */
  stacking: "series" | "hidden";
  /**
   * Floating-bar types carry `[start, end]` points: a default label would print "start,end",
   * so the control appears only when the block already formats its own labels.
   */
  dataLabelsRequireOwnConfig?: boolean;
};

const CATEGORICAL: CapabilityRow = {
  typeSwitch: true,
  aiConvertTargets: [],
  stacking: "series",
  legend: true,
  dataLabels: true,
  axes: true,
};

const SEGMENT: CapabilityRow = {
  typeSwitch: true,
  aiConvertTargets: [],
  stacking: "hidden",
  legend: true,
  dataLabels: true,
  axes: false,
};

const FLOATING_BAR = (targets: readonly string[]): CapabilityRow => ({
  typeSwitch: false,
  aiConvertTargets: targets,
  stacking: "hidden",
  legend: true,
  dataLabels: true,
  dataLabelsRequireOwnConfig: true,
  axes: true,
});

/**
 * One row per spec `chart_type` (the list the agent may emit, see web_dashboard_agent_utils).
 * Object-data types (points, cells, trees, flows, regions) get no data-label control: the
 * plugin's default label for an object is garbage, and the chart docs turn labels off there.
 */
export const CHART_CAPABILITIES = {
  bar: CATEGORICAL,
  horizontalBar: CATEGORICAL,
  line: CATEGORICAL,
  area: CATEGORICAL,
  pie: SEGMENT,
  doughnut: SEGMENT,
  funnel: SEGMENT,
  scatter: {
    typeSwitch: false,
    aiConvertTargets: ["line", "bar", "heatmap"],
    stacking: "hidden",
    legend: true,
    dataLabels: false,
    axes: true,
  },
  bubble: {
    typeSwitch: false,
    aiConvertTargets: ["scatter", "line", "bar"],
    stacking: "hidden",
    legend: true,
    dataLabels: false,
    axes: true,
  },
  heatmap: {
    typeSwitch: false,
    aiConvertTargets: ["bar", "line"],
    stacking: "hidden",
    legend: false,
    dataLabels: false,
    axes: true,
  },
  treemap: {
    typeSwitch: false,
    aiConvertTargets: ["pie", "bar"],
    stacking: "hidden",
    legend: false,
    dataLabels: false,
    axes: false,
  },
  sankey: {
    typeSwitch: false,
    aiConvertTargets: ["bar", "pie"],
    stacking: "hidden",
    legend: false,
    dataLabels: false,
    axes: false,
  },
  waterfall: FLOATING_BAR(["bar", "bridge"]),
  bridge: FLOATING_BAR(["bar", "waterfall"]),
  gantt: FLOATING_BAR(["bar"]),
  choropleth: {
    typeSwitch: false,
    aiConvertTargets: [],
    stacking: "hidden",
    legend: false,
    dataLabels: false,
    axes: false,
  },
  bubbleMap: {
    typeSwitch: false,
    aiConvertTargets: [],
    stacking: "hidden",
    legend: false,
    dataLabels: false,
    axes: false,
  },
} satisfies Record<string, CapabilityRow>;

export type KnownChartType = keyof typeof CHART_CAPABILITIES;

/** A chart_type the table doesn't know: only title/description and Edit in chat. */
const UNKNOWN: CapabilityRow = {
  typeSwitch: false,
  aiConvertTargets: [],
  stacking: "hidden",
  legend: false,
  dataLabels: false,
  axes: false,
};

/**
 * Which type to look up in the table. Structural types render as a plain Chart.js type —
 * waterfall/bridge/gantt are `type: "bar"` with floating `[start, end]` points, heatmap is
 * `type: "matrix"` — so the live chart's effective type would hand a waterfall the bar row
 * and offer a type switcher that destroys it. The spec `chart_type` wins whenever it names a
 * type the switcher cannot convert; otherwise the effective type is more truthful (a block
 * declared `bar` whose JS draws `indexAxis: "y"` is a horizontal bar).
 */
export function resolveCapabilityType(
  specChartType: string | undefined,
  effectiveType: string
): string {
  const specRow = specChartType
    ? (CHART_CAPABILITIES as Record<string, CapabilityRow>)[specChartType]
    : undefined;
  if (specRow && !specRow.typeSwitch) {
    return specChartType as string;
  }
  return effectiveType;
}

const capabilityRowFor = (chartType: string): CapabilityRow =>
  (CHART_CAPABILITIES as Record<string, CapabilityRow>)[chartType] ?? UNKNOWN;

/** Drawn on x/y scales, so the axis toggles apply. */
export const chartTypeHasAxes = (chartType: string): boolean => capabilityRowFor(chartType).axes;

/** Series can stack (the cartesian bar/line family). */
export const chartTypeStacks = (chartType: string): boolean =>
  capabilityRowFor(chartType).stacking === "series";

export function getChartCapabilities(
  chartType: string,
  ctx: ChartCapabilityContext
): ChartCapabilities {
  const row = capabilityRowFor(chartType);
  let stacking: StackingCapability = "hidden";
  if (row.stacking === "series" && ctx.seriesCount >= 2) {
    // Percent stacking rewrites every dataset as a share of the per-index total; across two
    // value axes that total mixes units, so only plain stacking is offered there.
    stacking = ctx.valueAxisCount > 1 ? "plain" : "full";
  }
  return {
    typeSwitch: row.typeSwitch,
    aiConvertTargets: row.aiConvertTargets,
    stacking,
    legend: row.legend,
    dataLabels: row.dataLabelsRequireOwnConfig ? ctx.hasOwnDatalabels : row.dataLabels,
    axes: row.axes,
  };
}
